import { useRef, useState } from "react";
import { Avatar } from "../Avatar/Avatar";
import EmailSignatureIcon from "../../assets/svg/email-signature-icon";
import PasswordIcon from "../../assets/svg/password-icon";
import LogoutIcon from "../../assets/svg/logout-icon";
import { rolesMapping } from "../../utils/constants/rolesMapping";
import Button from "../Button/Button";
import EditIcon from "../../assets/svg/edit-icon";
import { UserData } from "../../utils/global-types/global-types";
import { OutlookIntegration } from "../../redux/slices/authSlice";
import { Tooltip } from "../Tooltip/Tooltip";
import AvailabilityIcon from "../../assets/svg/availability-icon";
import CameraIcon from "../../assets/svg/camera-icon";
import OutlookImage from "../../assets/images/Outlook.png";
import CircularLoader from "../Loader/CircularLoader";
import CrossIcon from "../../assets/svg/cross-icon";
import ThreeDotsVerticalIcon from "../../assets/svg/three-dots-vertical-icon";
import { OutlookConnectStatus } from "./ProfileCard";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import DisconnectOutlookModal from "./DisconnectOutlookModal";

interface ProfileViewProps {
  userData: UserData;
  handleSignOut: () => void;
  setShowEditProfile: (show: boolean) => void;
  setShowChangePassword: (show: boolean) => void;
  handleOpenEmailSignatureModal: () => void;
  handleOpenAgentAvailabilityLeavesModal: () => void;
  handleConnectOutlook: () => void;
  outlookIntegration: OutlookIntegration | null;
  outlookConnectStatus: OutlookConnectStatus;
  showDisconnectModal: boolean;
  setShowDisconnectModal: (show: boolean) => void;
  handleDisconnectOutlook: () => void;
  isDisconnecting: boolean;
}

const ProfileView = ({
  userData,
  handleSignOut,
  setShowEditProfile,
  setShowChangePassword,
  handleOpenEmailSignatureModal,
  handleOpenAgentAvailabilityLeavesModal,
  handleConnectOutlook,
  outlookIntegration,
  outlookConnectStatus,
  showDisconnectModal,
  setShowDisconnectModal,
  handleDisconnectOutlook,
  isDisconnecting,
}: ProfileViewProps) => {
  const [showThreeDotMenu, setShowThreeDotMenu] = useState(false);
  const threeDotMenuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(threeDotMenuRef, () => setShowThreeDotMenu(false));
  return (
    <div className="flex flex-col gap-3 pt-2 px-2 pb-1.5">
      <div
        className="group flex flex-col gap-2 items-center mt-3 cursor-pointer"
        onClick={() => setShowEditProfile(true)}
      >
        <div className="relative flex items-center justify-center w-full">
          {userData?.profile_picture ? (
            <img
              src={userData?.profile_picture as string}
              alt="profile_picture"
              className="object-cover rounded-full w-[48px] h-[48px]"
            />
          ) : (
            <div className="relative">
              <Avatar text={userData?.name?.slice(0, 2)} size={48} />{" "}
              <Tooltip content="Upload profile picture">
                <div className="flex items-center justify-center w-4.5 h-4.5 absolute -bottom-1 right-0 bg-pure-white dark:bg-pure-white-dk rounded-full p-1 cursor-pointer">
                  <span>
                    <CameraIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
                  </span>
                </div>
              </Tooltip>
            </div>
          )}
        </div>
        <span className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk text-center break-words max-w-full">
          Hi, {userData?.name}
        </span>
        <div className="bg-pure-white dark:bg-pure-white-dk rounded-md border border-border-gray dark:border-border-gray-dk flex items-center gap-1 px-1.5 py-0.5 max-w-full">
          <span className="inline-block bg-verba-green-success dark:bg-verba-green-success-dk rounded-full w-2 h-2 shrink-0" />
          <Tooltip content="Assigned Role">
            <span className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk cursor-pointer break-words">
              {rolesMapping[userData?.role as keyof typeof rolesMapping]}
            </span>
          </Tooltip>
        </div>
        <div className="flex items-start w-full justify-center">
          <div className="flex justify-end min-w-0">
            <Tooltip content="Email address">
              <span className="text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk cursor-pointer text-right break-all truncate max-w-[180px]">
                {userData?.email}
              </span>
            </Tooltip>
          </div>
          <span className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk px-1.5 shrink-0">
            |
          </span>
          <div className="flex justify-start min-w-0">
            <Tooltip content="Phone number">
              <span className="text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk cursor-pointer text-left break-all truncate max-w-[180px]">
                {userData?.country_code} {userData?.phone_number || ""}
              </span>
            </Tooltip>
          </div>
        </div>
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            innerText="Edit"
            innerTextClass="mt-0.5 text-xs text-data dark:text-data-dk font-medium"
            buttonContainerClass="mb-4"
            iconLeft
            iconClass="w-4.5 h-4.5 text-data dark:text-data-dk"
            Icon={EditIcon}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1 mt-6">
        <div
          onClick={handleOpenAgentAvailabilityLeavesModal}
          className="w-full flex items-center gap-1 px-2 py-1.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg cursor-pointer"
        >
          <AvailabilityIcon className="w-4.5 h-4.5 text-primary-blue-dk dark:text-primary-gray-lightest" />
          <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
            Mark Unavailability & Leaves
          </span>
        </div>
        <div
          onClick={handleOpenEmailSignatureModal}
          className="w-full flex items-center gap-1 px-2 py-1.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg cursor-pointer"
        >
          <EmailSignatureIcon className="w-4.5 h-4.5 text-primary-blue-dk dark:text-primary-gray-lightest" />
          <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
            Email Signature
          </span>
        </div>
        <div
          className="w-full flex items-center gap-1 px-2 py-1.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg cursor-pointer"
          onClick={() => {
            setShowChangePassword(true);
          }}
        >
          <PasswordIcon className="w-4.5 h-4.5 text-primary-blue-dk dark:text-primary-gray-lightest" />
          <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
            Change Password
          </span>
        </div>
        {outlookIntegration?.is_integration_available && (
          <>
            <div className="border-t border-white dark:border-gray-25-dk my-0.5" />
            <div className="w-full flex flex-col gap-1.5 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-2.5 py-2 shadow-card-subtle">
              <div className="flex items-center gap-1">
                <img
                  src={OutlookImage}
                  alt="Outlook"
                  className="w-4.5 h-4.5 shrink-0 object-contain"
                />
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                    Outlook Integration
                  </span>
                  <span className="text-xs font-normal text-data dark:text-data-dk leading-normal">
                    Send emails using your Outlook mailbox
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                {outlookIntegration?.integration_status ? (
                  <>
                    <span className="flex items-center gap-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-md px-1.5 py-0.5 shadow-xs">
                      <span className="inline-block bg-verba-green-success dark:bg-verba-green-success-dk rounded-full w-2 h-2" />
                      <span className="text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk leading-[18px]">
                        Connected
                      </span>
                    </span>
                    <div className="relative" ref={threeDotMenuRef}>
                      <button
                        className="p-0.5 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer"
                        onClick={() => setShowThreeDotMenu((prev) => !prev)}
                      >
                        <ThreeDotsVerticalIcon className="w-4 h-4 text-data dark:text-data-dk" />
                      </button>
                      {showThreeDotMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-lg z-10 min-w-[180px]">
                          <button
                            className="w-full flex items-center gap-1.5 px-3 py-2 text-left hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg cursor-pointer"
                            onClick={() => {
                              setShowThreeDotMenu(false);
                              setShowDisconnectModal(true);
                            }}
                          >
                            <CrossIcon className="w-4 h-4 text-red-500" />
                            <span className="text-md font-medium text-red-500">
                              Disconnect Outlook
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : outlookConnectStatus === "connecting" ? (
                  <span className="flex items-center gap-1">
                    <CircularLoader
                      size={14}
                      thickness={2}
                      className="text-gray-placeholder dark:text-gray-placeholder-dk"
                    />
                    <span className="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk whitespace-nowrap">
                      Connecting...
                    </span>
                  </span>
                ) : outlookConnectStatus === "failed" ? (
                  <span className="flex items-center gap-2">
                    <span className="flex items-center gap-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-md px-1.5 py-0.5 shadow-xs">
                      <CrossIcon className="w-3 h-3 text-red-500" />
                      <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-[18px]">
                        Failed
                      </span>
                    </span>
                    <span
                      className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk whitespace-nowrap hover:underline cursor-pointer"
                      onClick={handleConnectOutlook}
                    >
                      {"Retry >"}
                    </span>
                  </span>
                ) : (
                  <span
                    className="text-md font-semibold text-verba-blue dark:text-heading-text-dk whitespace-nowrap hover:underline cursor-pointer"
                    onClick={handleConnectOutlook}
                  >
                    {"Connect >"}
                  </span>
                )}
              </div>
            </div>
          </>
        )}
        <DisconnectOutlookModal
          isOpen={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          onConfirm={handleDisconnectOutlook}
          isLoading={isDisconnecting}
        />
        <div className="border-t border-white dark:border-gray-25-dk my-0.5" />
        <button
          className="w-full flex items-center gap-1 px-2 py-1.5 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg cursor-pointer"
          onClick={handleSignOut}
        >
          <LogoutIcon className="w-4.5 h-4.5 text-primary-blue-dk dark:text-primary-gray-lightest" />
          <span className="text-md  font-normal text-heading-text dark:text-heading-text-dk">
            Logout
          </span>
        </button>
      </div>
    </div>
  );
};

export default ProfileView;
